import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getOrderMemos, type OrderMemo, type OrderMemos } from "../../services/studioSalesApi";
import { canUseParcels } from "../sales/phone/ParcelsCard";
import { STAGES, isParcelsOff, overdueText, stageText } from "../sales/phone/parcelModel";

const MEMO_STATUS: Record<string, string> = {
  DRAFT: "Draft",
  READY_FOR_BILLING: "Ready to bill",
  INVOICE_GENERATED: "Billed",
  BILLED: "Billed",
  OUT_ON_APPROVAL: "Out on approval",
  CLOSED: "Closed",
  CANCELLED: "Cancelled",
};

const memoTitle = (m: OrderMemo) => `${m.type === "APPROVAL" ? "Approval" : "Sale"} ${m.memoCode}`;

/**
 * Read-only "Memos & parcels" on a studio order: each sale or approval memo linked to it, with its parcels'
 * courier, AWB, stage and the 15-day flag. Links are made on the memo screen. Nothing renders while the feature is
 * off, for anyone but studio staff, or before the order exists.
 */
const OrderMemosPanel = ({ orderId, role }: { orderId: string; role?: string | null }) => {
  const allowed = canUseParcels(role || undefined);
  const [state, setState] = useState<"off" | "loading" | "error" | "ready">(allowed && orderId ? "loading" : "off");
  const [data, setData] = useState<OrderMemos | null>(null);
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (!allowed || !orderId) {
      setState("off");
      return;
    }
    let live = true;
    setState("loading");
    getOrderMemos(orderId)
      .then((res) => {
        if (!live) return;
        if (isParcelsOff(res) || res?.status === 403) return setState("off");
        if (!res?.success || !res.data) return setState("error");
        setData(res.data);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => { live = false; };
  }, [allowed, orderId, round]);

  const retry = useCallback(() => setRound((n) => n + 1), []);
  if (state === "off") return null;

  return (
    <section className="geo-card order-memos" aria-labelledby="order-memos-title">
      <h3 id="order-memos-title" className="order-memos__title">Memos &amp; parcels</h3>
      {state === "loading" ? <p className="gi-au-hint" role="status">Loading memos…</p> : null}
      {state === "error" ? (
        <p className="gi-au-hint" role="status">
          Couldn't load the memos. <button type="button" className="order-memos__retry" onClick={retry}>Try again</button>
        </p>
      ) : null}
      {state === "ready" && data && data.memos.length === 0 ? (
        <p className="gi-au-hint">No memo linked yet. Open a sale or approval memo and choose Link to order.</p>
      ) : null}
      {state === "ready" && data && data.memos.length ? (
        <ul className="order-memos__list">
          {data.memos.map((m) => (
            <li key={m.id} className="order-memos__memo">
              <div className="order-memos__head">
                <Link to={`/sales/memos/${m.id}`} className="order-memos__code">{memoTitle(m)}</Link>
                <span className="order-memos__meta">
                  {[MEMO_STATUS[m.status] || m.status, m.invoiceNumber ? `Invoice ${m.invoiceNumber}` : ""].filter(Boolean).join(" · ")}
                </span>
              </div>
              {m.parcels.length === 0 ? (
                <p className="order-memos__none">No parcel yet</p>
              ) : (
                <ul className="order-memos__parcels">
                  {m.parcels.map((p) => {
                    const info = STAGES[p.stage] || STAGES.BOOKED;
                    const late = overdueText(p);
                    return (
                      <li key={p.id} className="order-memos__parcel">
                        <span className={`order-memos__stage is-${info.tone}`}><span aria-hidden="true">{info.glyph} </span>{stageText(p)}</span>
                        <span className="order-memos__awb">{p.courierName} · <span className="mono">{p.awbNumber}</span></span>
                        {late ? <span className="order-memos__late"><span aria-hidden="true">! </span>{late}</span> : null}
                        {p.trackingUrl ? (
                          <a className="order-memos__track" href={p.trackingUrl} target="_blank" rel="noopener noreferrer">Open courier page</a>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {state === "ready" && data?.truncated ? <p className="gi-au-hint">Showing the first 50 memos.</p> : null}
    </section>
  );
};

export default OrderMemosPanel;
